import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { Toaster } from "./components/ui/toaster";

import AppShell from "./layouts/AppShell";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import Dashboard from "./pages/Dashboard";
import AskAI from "./pages/AskAI";
import SettingsPage from "./pages/Settings";
import AppsDirectory from "./pages/AppsDirectory";
import UCVigil from "./pages/UCVigil";
import ActivityLogs from "./pages/ActivityLogs";
import OnePassword from "./pages/OnePassword";
import IndustryNews from "./pages/IndustryNews";
import NSInsight from "./pages/NSInsight";
import PentestApp from "./pages/PentestApp";
import IncidentIO from "./pages/IncidentIO";
import SecurityReports from "./pages/SecurityReports";
import ThreatLockerApp from "./pages/ThreatLockerApp";
import PingPlotterApp from "./pages/PingPlotterApp";
import Compliance from "./pages/Compliance";
import EmployeePortal from "./pages/EmployeePortal";
import AuditorPortal from "./pages/AuditorPortal";
import TrustCenter from "./pages/TrustCenter";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force users with a temp password to update it before accessing any protected route.
  if (user.requires_password_change) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  // CRITICAL: detect the Google OAuth session_id fragment synchronously during
  // render (not in a useEffect, which runs too late and races ProtectedRoute).
  // Must read from useLocation().hash, not window.location.hash directly.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/trust" element={<TrustCenter />} />
      <Route
        path="/portal/*"
        element={
          <Protected>
            <EmployeePortal />
          </Protected>
        }
      />
      <Route
        path="/auditor/*"
        element={
          <Protected>
            <AuditorPortal />
          </Protected>
        }
      />
      <Route
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/apps" element={<AppsDirectory />} />
        <Route path="/apps/ns-insight" element={<NSInsight />} />
        <Route path="/apps/pentest" element={<Navigate to="/apps/vulnerability" replace />} />
        <Route path="/apps/vulnerability" element={<PentestApp />} />
        <Route path="/apps/incident-io" element={<IncidentIO />} />
        <Route path="/apps/threatlocker" element={<ThreatLockerApp />} />
        <Route path="/apps/pingplotter" element={<PingPlotterApp />} />
        <Route path="/apps/compliance" element={<Compliance />} />
        <Route path="/analytics/security" element={<SecurityReports />} />
        <Route path="/uc-vigil" element={<UCVigil />} />
        <Route path="/1password" element={<OnePassword />} />
        <Route path="/ask-ai" element={<AskAI />} />
        <Route path="/news" element={<IndustryNews />} />
        <Route path="/activity" element={<ActivityLogs />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/:tab" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster />
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
